import { apiClient } from "shared/api/client/orval";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type Organization = {
  id: number;
  name: string;
  website: string;
  country: string;
  bio: string;
  youTubeChannel: string;
  xcom: string;
  instagram: string;
  tikTok: string;
  ownerUserId: number;
};

export type OrganizationMember = {
  id: number;
  userId: number;
  email: string;
  role: string;
  invitationSent: boolean;
  label: string | null;
};

export type OrganizationDetailsPayload = {
  name: string;
  website?: string;
  country?: string;
  bio?: string;
  youTubeChannel?: string;
  xcom?: string;
  instagram?: string;
  tikTok?: string;
};

export type InviteMemberPayload = {
  email: string;
  role?: "Admin" | "Member";
};

/* -------------------------------------------------------------------------- */
/* Normalizers (tolerate .NET `$values` wrappers and PascalCase fields)       */
/* -------------------------------------------------------------------------- */

type Rec = Record<string, unknown>;

const asRec = (v: unknown): Rec =>
  v && typeof v === "object" ? (v as Rec) : {};

function asList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const o = asRec(data);
  for (const key of ["$values", "items", "data", "organizations", "members"]) {
    if (Array.isArray(o[key])) return o[key] as unknown[];
    if (o[key] && typeof o[key] === "object") return asList(o[key]);
  }
  return [];
}

const pick = (o: Rec, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
};

const str = (v: unknown, fallback = ""): string =>
  v === undefined || v === null ? fallback : String(v);

export function normalizeOrganization(raw: unknown): Organization {
  const o = asRec(raw);
  return {
    id: Number(pick(o, "id", "Id", "organizationId", "OrganizationId") ?? 0),
    name: str(pick(o, "name", "Name")),
    website: str(pick(o, "website", "Website")),
    country: str(pick(o, "country", "Country")),
    bio: str(pick(o, "bio", "Bio")),
    youTubeChannel: str(pick(o, "youTubeChannel", "youtubeChannel", "YouTubeChannel")),
    xcom: str(pick(o, "xcom", "Xcom", "xCom")),
    instagram: str(pick(o, "instagram", "Instagram")),
    tikTok: str(pick(o, "tikTok", "tiktok", "TikTok")),
    ownerUserId: Number(pick(o, "ownerUserId", "OwnerUserId") ?? 0),
  };
}

export function normalizeMember(raw: unknown): OrganizationMember {
  const o = asRec(raw);
  const invitationSent = Boolean(pick(o, "invitationSent", "InvitationSent") ?? false);
  return {
    id: Number(pick(o, "id", "Id", "memberId", "MemberId") ?? 0),
    userId: Number(pick(o, "userId", "UserId") ?? 0),
    email: str(pick(o, "email", "Email")),
    role: str(pick(o, "role", "Role"), "Member"),
    invitationSent,
    label: (pick(o, "label", "Label") as string | undefined) ?? (invitationSent ? "Invited" : null),
  };
}

/* -------------------------------------------------------------------------- */
/* Organizations                                                              */
/* -------------------------------------------------------------------------- */

export async function getMyOrganizations(): Promise<Organization[]> {
  const res = await apiClient.get("/api/Org/my-organizations");
  return asList(res.data).map(normalizeOrganization);
}

export async function getOrganization(organizationId: number): Promise<Organization> {
  const res = await apiClient.get(`/api/Org/${organizationId}`);
  return normalizeOrganization(res.data);
}

export async function createOrganization(
  payload: OrganizationDetailsPayload
): Promise<Organization> {
  const res = await apiClient.post("/api/Org/create-organization", payload);
  const body = asRec(res.data);
  return normalizeOrganization(body.organization ?? res.data);
}

export async function updateOrganization(
  organizationId: number,
  payload: Partial<OrganizationDetailsPayload>
): Promise<void> {
  await apiClient.put(`/api/Org/${organizationId}/edit`, payload);
}

/* -------------------------------------------------------------------------- */
/* Members                                                                    */
/* -------------------------------------------------------------------------- */

export async function getOrganizationMembers(
  organizationId: number
): Promise<OrganizationMember[]> {
  const res = await apiClient.get(`/api/Org/${organizationId}/members`);
  return asList(res.data).map(normalizeMember);
}

export async function inviteOrganizationMember(
  organizationId: number,
  payload: InviteMemberPayload
): Promise<OrganizationMember> {
  const res = await apiClient.post(`/api/Org/${organizationId}/members/invite`, {
    email: payload.email.trim(),
    role: payload.role ?? "Member",
  });
  const body = asRec(res.data);
  return normalizeMember(body.member ?? res.data);
}

export async function removeOrganizationMember(
  organizationId: number,
  memberId: number
): Promise<void> {
  await apiClient.delete(`/api/Org/${organizationId}/members/remove/${memberId}`);
}
